import React, { useState, useEffect } from 'react';
import { validateName, validateEmail, validatePhone, sanitizeInput } from '../utils/validation';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';
import { contactService } from '../services';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
}

interface CompanyData {
  companyName: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  businessHours?: {
    [key: string]: {
      open?: string;
      close?: string;
      closed?: boolean;
    };
  };
}

const Contact: React.FC = () => {
  const { showToast } = useToast();
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const data = await apiRequest<CompanyData>(API_ENDPOINTS.COMPANY.PUBLIC);
        setCompanyData(data);
      } catch (error) {
        console.error('Error loading company info:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCompanyInfo();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    // Apply specific sanitization and limits
    if (name === 'phone') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'name') {
      sanitizedValue = sanitizeInput(value).slice(0, 50);
    } else if (name === 'email') {
      sanitizedValue = sanitizeInput(value).slice(0, 254);
    } else if (name === 'message') {
      sanitizedValue = sanitizeInput(value).slice(0, 1000);
    } else {
      sanitizedValue = sanitizeInput(value);
    }

    setFormData({
      ...formData,
      [name]: sanitizedValue
    });

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate name
    const nameValidation = validateName(formData.name, 'Full name');
    if (!nameValidation.isValid) {
      newErrors.name = nameValidation.error;
    }

    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }

    // Validate phone (optional but if provided should be valid)
    if (formData.phone) {
      const phoneValidation = validatePhone(formData.phone);
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.error;
      }
    }

    // Validate subject
    if (!formData.subject) {
      newErrors.subject = 'Please select a subject';
    }

    // Validate message
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const contactFormData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone || undefined,
        subject: formData.subject,
        message: formData.message.trim(),
      };

      const response = await contactService.submitContactForm(contactFormData);
      
      if (response.success) {
        showToast('Thank you for your message! We\'ll get back to you within 24 hours.', 'success');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setErrors({});

        // Track form submission (in a real app, this would go to analytics)
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'contact_form_submit', {
            event_category: 'engagement',
            event_label: formData.subject
          });
        }
      } else {
        showToast(response.error || 'There was an error sending your message. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      showToast('There was an error sending your message. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container py-16 max-w-6xl">
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold text-neutral-900 mb-6">Contact Us</h1>
        <p className="text-xl text-gray-600">
          Get in touch with our team for any questions about our products or services
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <h2 className="text-3xl font-bold text-neutral-900 mb-8">Get in Touch</h2>
          
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading contact information...</div>
            </div>
          ) : companyData ? (
            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="text-2xl">📍</div>
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-1">Address</h3>
                  <p className="text-gray-600">
                    {companyData.address.street}<br />
                    {companyData.address.city}, {companyData.address.state} {companyData.address.zipCode}<br />
                    {companyData.address.country}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="text-2xl">📞</div>
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-1">Phone</h3>
                  <p className="text-gray-600">{companyData.phone}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="text-2xl">✉️</div>
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-1">Email</h3>
                  <p className="text-gray-600">{companyData.email}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="text-2xl">🕒</div>
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-1">Business Hours</h3>
                  <div className="text-gray-600">
                    {companyData.businessHours && Object.entries(companyData.businessHours).map(([day, hours]) => {
                      const dayCapitalized = day.charAt(0).toUpperCase() + day.slice(1);
                      if (hours.closed) {
                        return <p key={day}>{dayCapitalized}: Closed</p>;
                      }
                      return <p key={day}>{dayCapitalized}: {hours.open} - {hours.close}</p>;
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6 mb-8">
              <p className="text-gray-600">Unable to load contact information. Please try again later.</p>
            </div>
          )}

          <div className="bg-accent-100 rounded-lg p-6">
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Safety Notice</h3>
            <p className="text-gray-700 text-sm">
              Please handle all fireworks with care and follow safety guidelines. 
              Ensure adult supervision for children and use in open areas only.
            </p>
          </div>
        </div>

        <div>
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">Send us a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="+91 80727 89313"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                      errors.subject ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a subject</option>
                    <option value="product-inquiry">Product Inquiry</option>
                    <option value="enquiry-support">Enquiry Support</option>
                    <option value="safety-question">Safety Question</option>
                    <option value="bulk-enquiry">Bulk Enquiry</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 ${
                    errors.message ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Tell us how we can help you..."
                ></textarea>
                {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-colors ${
                  isSubmitting
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-accent-600 text-white hover:bg-accent-700'
                }`}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;